import permissionsConfig from '@/configs/permissions/roles.json';

export type TeamPermissionKey = string;
export type TeamRole = string;

export interface RoleConfig {
  label: string;
  description: string;
  isPosRole: boolean;
  isAssignable: boolean;
  permissions: TeamPermissionKey[];
  implicitGrants: TeamPermissionKey[];
  notes: string;
}

export interface PosBundleConfig {
  label: string;
  permissions: TeamPermissionKey[];
}

interface PermissionsConfig {
  roles: Record<string, RoleConfig>;
  permissionKeys: {
    core_dashboard: TeamPermissionKey[];
    operations: TeamPermissionKey[];
    pos: TeamPermissionKey[];
    courier: TeamPermissionKey[];
    ai: TeamPermissionKey[];
  };
  posBundles: Record<string, PosBundleConfig>;
  inheritanceRules: Array<{
    trigger: string;
    grants: TeamPermissionKey[];
    description: string;
  }>;
}

const config = permissionsConfig as unknown as PermissionsConfig;

export function getRole(role: string): RoleConfig | undefined {
  return config.roles[role];
}

export function getRolePermissions(role: string): TeamPermissionKey[] {
  const roleConfig = getRole(role);
  if (!roleConfig) return [];
  const permissions = new Set(roleConfig.permissions);
  for (const grant of roleConfig.implicitGrants) {
    permissions.add(grant);
  }
  return [...permissions];
}

export function hasPermission(role: string, permission: TeamPermissionKey): boolean {
  return getRolePermissions(role).includes(permission);
}

export function hasAnyPermission(role: string, permissions: TeamPermissionKey[]): boolean {
  const rolePerms = getRolePermissions(role);
  return permissions.some(p => rolePerms.includes(p));
}

export function hasAllPermissions(role: string, permissions: TeamPermissionKey[]): boolean {
  const rolePerms = getRolePermissions(role);
  return permissions.every(p => rolePerms.includes(p));
}

export function getPosBundle(bundle: string): PosBundleConfig | undefined {
  return config.posBundles[bundle];
}

export function getPosBundlePermissions(bundle: string): TeamPermissionKey[] {
  return getPosBundle(bundle)?.permissions ?? [];
}

export function getCoreDashboardPermissions(): TeamPermissionKey[] {
  return [...config.permissionKeys.core_dashboard];
}

export function getOperationsPermissions(): TeamPermissionKey[] {
  return [...config.permissionKeys.operations];
}

export function getPosPermissions(): TeamPermissionKey[] {
  return [...config.permissionKeys.pos];
}

export function getCourierPermissions(): TeamPermissionKey[] {
  return [...config.permissionKeys.courier];
}

export function getAiPermissions(): TeamPermissionKey[] {
  return [...config.permissionKeys.ai];
}

export function isPosRole(role: string): boolean {
  return getRole(role)?.isPosRole ?? false;
}

export function isAssignableRole(role: string): boolean {
  return getRole(role)?.isAssignable ?? false;
}

export function getAllRoles(): string[] {
  return Object.keys(config.roles);
}

export function getAllPosBundles(): string[] {
  return Object.keys(config.posBundles);
}

export function applyInheritanceRules(permissions: TeamPermissionKey[]): TeamPermissionKey[] {
  const result = new Set(permissions);
  for (const rule of config.inheritanceRules) {
    if (result.has(rule.trigger)) {
      for (const grant of rule.grants) {
        result.add(grant);
      }
    }
  }
  return [...result];
}

export { config as permissionsConfig };
